import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from './styles';
import AvailabilitySnapshotModal from './AvailabilitySnapshotModal';
import AppointmentTable from '@/ctComponents/AppointmentTable';
import LoadingV2 from '@/components/Loading';
import { CustomGrid, CustomGridItem } from '@/ctComponents/Grid';
import CustomSelectEx from '@/ctComponents/CustomSelectEx';
import {
  getAvailabilityHistory,
  getAvailabilityHistoryById,
  NewAppointmentsFilter,
  SaveApiResMessage,
} from '@/actions';
import { Icon } from '@/components/Icon';
import { ReactSVG } from 'react-svg';
import { Icons } from '@/assets';
import tableNavStyles from '@/ctComponents/AppointmentTable/AppointmentTable.module.css';
import timelineNavStyles from './AvailHistoryTimelineNav.module.css';
import {
  getTimelineCirclePaintFromApiColor,
  TIMELINE_COLOR_BORDER_GRAY_FILL,
  TIMELINE_COLOR_BORDER_GREEN_FILL,
  TIMELINE_COLOR_GRAY_RING,
  TIMELINE_COLOR_GRAY_SOLID,
  TIMELINE_COLOR_GREEN_RING,
  TIMELINE_COLOR_GREEN_SOLID,
  TIMELINE_NOW_DOT_BORDER,
} from '@/utils/availHistoryTimelinePaint';

/** حداکثر روز به عقب برای `day_offset` در API availability-history */
const MAX_AVAIL_HISTORY_DAY_OFFSET = 7;

/**
 * پاسخ لیست گاهی `{ message, data: { now, availabilityHistories } }` است و `getAvailabilityHistory`
 * همان `data` را برمی‌گرداند؛ گاهی هم خود آبجکت payload است. این تابع لایهٔ درست را برمی‌گرداند
 * تا `now` و جدول availability همیشه از یک منبع هم‌خوان خوانده شوند.
 */
function pickAvailabilityHistoryPayloadRoot(res) {
  if (!res || typeof res !== 'object') return res;
  const inner = res.data;
  if (
    inner &&
    typeof inner === 'object' &&
    !Array.isArray(inner) &&
    (Object.prototype.hasOwnProperty.call(inner, 'now') ||
      Object.prototype.hasOwnProperty.call(inner, 'availabilityHistories') ||
      Object.prototype.hasOwnProperty.call(inner, 'availability_histories'))
  ) {
    return inner;
  }
  return res;
}

function pickHistoryByIdPayloadRoot(res) {
  if (!res || typeof res !== 'object') return res;
  const inner = res.data;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    if (
      inner.availability != null ||
      inner.terminals != null ||
      inner.id != null ||
      Array.isArray(inner.slots)
    ) {
      return inner;
    }
  }
  return res;
}

function isRequestAbortedError(err) {
  return (
    err?.name === 'CanceledError' ||
    err?.code === 'ERR_CANCELED' ||
    /aborted|canceled|cancelled/i.test(String(err?.message || ''))
  );
}

/**
 * فیلتر Terminal از `data.now.terminals` در پاسخ availability-history (همان آبجکت terminalهای جدول).
 */
function buildTerminalFilterOptionsFromNowTerminals(terminals) {
  if (!terminals || typeof terminals !== 'object') {
    return [{ label: 'All Terminals', value: null }];
  }
  const entries = Array.isArray(terminals)
    ? terminals
    : Object.values(terminals);
  const mapped = entries
    .filter((t) => t && (t.id != null || t.slug != null || t.name != null))
    .map((t) => {
      const idNum = Number(t.id);
      return {
        label: (t.slug || t.name || String(t.id ?? '')).trim() || String(t.id),
        value: Number.isFinite(idNum) ? idNum : t.id,
      };
    });
  mapped.sort((a, b) =>
    String(a.label).localeCompare(String(b.label), undefined, {
      sensitivity: 'base',
    })
  );
  return [{ label: 'All Terminals', value: null }, ...mapped];
}

/**
 * مقدار ثابت `data-avail-history-id` برای نقطهٔ now (hover همگام بعد از لود؛ داخل historyById نیست).
 */
const AVAIL_HISTORY_NOW_DOT_ID = '__avail_now__';

/**
 * متن خام هر آیتم `slots[]` برای مودال snapshot — بدون تجزیهٔ تاریخ/زمان/تعداد.
 * رشته: همان رشته؛ آبجکت: فیلدهای متنی رایج یا در نهایت JSON همان payload.
 */
const snapshotSlotDisplayText = (raw) => {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s || /no\s+appointments?\s+available/i.test(s)) return null;
    return s;
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const t =
      raw.slot_text ??
      raw.slotText ??
      raw.text ??
      raw.label ??
      raw.message ??
      raw.description;
    if (t != null && String(t).trim()) return String(t).trim();
    try {
      return JSON.stringify(raw);
    } catch {
      return String(raw);
    }
  }
  const s = String(raw).trim();
  return s || null;
};

const AvailHistory = () => {
  const cls = styles();

  // Drawer/Popup in this project uses extremely large zIndex values.
  // To ensure hover/click highlights and the tooltip are visible over it,
  // we match that scale here.
  const VERY_HIGH_ZINDEX = '99999999999999999999999999999';

  const [loading, setLoading] = useState(false);
  /** فقط هنگام گرفتن جزئیات اسنپ‌شات (کلیک روی دایره) — بدون لود کل صفحه */
  const [availabilityTableLoading, setAvailabilityTableLoading] =
    useState(false);
  const [sizeOptions, setSizeOptions] = useState([]);
  const [SSLOptions, setSSLOptions] = useState([]);
  const [terminalOptions, setTerminalOptions] = useState([
    { label: 'All Terminals', value: null },
  ]);
  const [selected, setSelected] = useState({
    SSL: { label: null, value: null },
    Size: { label: null, value: null },
    Terminal: { label: 'All Terminals', value: null },
    Container: {},
  });

  /** همان `cabins` از NewAppointmentsFilter — کلید `c{ssl}_s{size}` → id کابین برای `available-apt-in` */
  const [cabinsMap, setCabinsMap] = useState({});

  const [appointmentData, setAppointmentData] = useState([]);
  const [shift, setShift] = useState(true); // true means shift1 active (AppointmentTable header highlight)
  const [startDate, setStartDate] = useState(null);
  // Axis window for timeline (24h). Must be stable while snapshot changes.
  const [axisWindow, setAxisWindow] = useState(null);
  const [tableSubTitle, setTableSubTitle] = useState('');
  const [histories, setHistories] = useState(null);
  const [currentHistory, setCurrentHistory] = useState(null);
  const [availabilitySnapshotModal, setAvailabilitySnapshotModal] =
    useState(null);
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);
  const nowSnapshotRef = useRef(null);
  const [hoveredHistory, setHoveredHistory] = useState(null);
  const mouseClientPosRef = useRef({ x: 0, y: 0 });
  const [hoverTooltipPos, setHoverTooltipPos] = useState({ top: 0, left: 0 });
  const timelineScrollRef = useRef(null);
  const timelineInnerScrollRef = useRef(null);
  const tooltipRef = useRef(null);
  /** 0 = امروز (`day_offset` نفرستیم)؛ 1…7 = مقدار `day_offset` در API */
  const dayOffsetRef = useRef(0);
  const [dayOffset, setDayOffset] = useState(0);
  /** جلوگیری از race: پاسخ قدیمی‌تر نباید جدول «now» را بعد از fetch جدید بازنویسی کند */
  const availabilityListAbortRef = useRef(null);
  const availabilityByIdAbortRef = useRef(null);

  const [timelineScrollEdges, setTimelineScrollEdges] = useState({
    atStart: true,
    atEnd: true,
  });

  /** همان منطق NewAppointment: `available-apt-in/{cabinId}/...` */
  const cabinId = useMemo(() => {
    const ssl = selected?.SSL?.value;
    const size = selected?.Size?.value;
    if (ssl == null || size == null) return undefined;
    const key = `c${ssl}_s${size}`;
    return cabinsMap?.[key];
  }, [cabinsMap, selected?.SSL?.value, selected?.Size?.value]);

  const refreshTimelineScrollEdges = useCallback(() => {
    const el = timelineInnerScrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = Math.max(0, scrollWidth - clientWidth);
    const atStart = scrollLeft <= 2;
    const atEnd = maxScroll <= 2 || scrollLeft >= maxScroll - 2;
    setTimelineScrollEdges((prev) =>
      prev.atStart === atStart && prev.atEnd === atEnd
        ? prev
        : { atStart, atEnd }
    );
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      mouseClientPosRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  useEffect(() => {
    if (loading) setHoveredHistory(null);
  }, [loading]);

  const buildLast24HoursSubtitle = (nowData) => {
    if (!nowData || typeof nowData !== 'object') return '';

    const last24 = nowData?.last_24_hours ?? nowData?.last24_hours ?? {};

    // Be defensive: backend field names can differ
    const checks =
      nowData?.checks_last_24_hours ??
      nowData?.last_24_hours_checks ??
      last24?.checks ??
      nowData?.checks ??
      null;

    const withSlots =
      nowData?.with_slots_last_24_hours ??
      nowData?.last_24_hours_with_slots ??
      last24?.with_slots ??
      nowData?.with_slots ??
      null;

    const noSlots =
      nowData?.no_slots_last_24_hours ??
      nowData?.last_24_hours_no_slots ??
      last24?.no_slots ??
      nowData?.no_slots ??
      null;

    if (
      checks !== null &&
      withSlots !== null &&
      noSlots !== null &&
      checks !== undefined &&
      withSlots !== undefined &&
      noSlots !== undefined
    ) {
      // Match Figma exactly: "Last 24 hours • X checks • Y with slots · Z no slots"
      return `Last 24 hours • ${checks} checks • ${withSlots} with slots · ${noSlots} no slots`;
    }

    // If backend already provides a ready-to-show subtitle, prefer it
    return (
      nowData?.last_24_hours_subtitle ??
      nowData?.subtitle ??
      nowData?.last24_hours_subtitle ??
      ''
    );
  };

  const computeLast24HoursSubtitleFromHistories = (historiesState) => {
    const list =
      historiesState?.data && Array.isArray(historiesState.data)
        ? historiesState.data
        : Array.isArray(historiesState)
        ? historiesState
        : null;

    if (!list || list.length === 0) return '';

    const withSlots = list.filter(
      (x) => Array.isArray(x?.slots) && x.slots.length > 0
    ).length;

    const checks = list.length;
    const noSlots = checks - withSlots;

    return `Last 24 hours • ${checks} checks • ${withSlots} with slots · ${noSlots} no slots`;
  };

  const loadLookups = () => {
    // get specifications + companies (same structure used in NewAppointment.js)
    NewAppointmentsFilter({ company: null, specifiation: null })
      .then((res) => {
        const specs = res?.data?.specifications || [];
        const companies = res?.data?.companies || [];

        const mappedSizes = specs.map(({ id, size, name, model }) => ({
          value: id,
          label: `${size}${name ? '-' + name : ''}-${model}`,
        }));

        const mappedSSls = companies.map(({ id, name }) => ({
          value: id,
          label: name,
        }));

        setSizeOptions(mappedSizes);
        setSSLOptions(mappedSSls);
        setCabinsMap(res?.data?.cabins ?? {});

        // Default selections: 20-Dry + MSC
        // We match by label content because the exact formatting of size/spec labels
        // (e.g. "20-HC-Dry", "20-Dry", etc) may vary.
        const defaultSize = mappedSizes.find(
          (opt) => /20/i.test(opt?.label || '') && /dry/i.test(opt?.label || '')
        ) ||
          mappedSizes?.[0] || { value: null, label: null };

        const defaultSsl = mappedSSls.find((opt) =>
          /msc/i.test(opt?.label || '')
        ) ||
          mappedSSls?.[0] || { value: null, label: null };

        setSelected((prev) => ({
          ...prev,
          Size: defaultSize,
          SSL: defaultSsl,
        }));
      })
      .catch((err) => {
        SaveApiResMessage({
          title: 'Error',
          message: err,
          mode: 'warning',
        });
      });
  };

  const mapAvailabilityToAppointmentData = (
    availabilityData,
    terminalsData
  ) => {
    if (!availabilityData || !terminalsData) return [];

    // Create local copy because we will mutate keys (id -> slug)
    const terminalsCopy = JSON.parse(JSON.stringify(terminalsData || {}));

    const terminalSlugs = Object.fromEntries(
      Object.entries(terminalsCopy).map(([id, terminal]) => [id, terminal.slug])
    );

    Object.keys(terminalsCopy).forEach((terminalId) => {
      const terminalSlug = terminalSlugs[terminalId];
      if (!terminalSlug) return;

      terminalsCopy[terminalSlug] = {
        ...terminalsCopy[terminalId],
        gate_schedules: {},
      };
      delete terminalsCopy[terminalId];

      Object.keys(availabilityData).forEach((date) => {
        terminalsCopy[terminalSlug].gate_schedules[date] = {};

        const byTerminal = availabilityData?.[date]?.[terminalId];
        if (!byTerminal) return;

        Object.keys(byTerminal).forEach((shiftKey) => {
          const shiftObj = byTerminal?.[shiftKey];
          if (!shiftObj) return;

          const status = shiftObj.status;
          terminalsCopy[terminalSlug].gate_schedules[date][shiftKey] = {
            single: status,
            dual: status === 1 || status === 2 ? 1 : status,
            available_count: shiftObj.available_count ?? 0,
            booked_count: shiftObj.booked_count ?? 0,
            bg_highlight: shiftObj.bg_highlight ?? null,
            status: shiftObj.status ?? null,
            gate_status: shiftObj.gate_status ?? null,
            gate_update: shiftObj.gate_update ?? null,
            restriction_status: shiftObj.restriction_status ?? null,
            restriction_update: shiftObj.restriction_update ?? null,
          };
        });
      });
    });

    return Object.values(terminalsCopy);
  };

  const fetchAvailability = () => {
    if (!selected?.SSL?.value || !selected?.Size?.value) return;

    if (availabilityListAbortRef.current) {
      availabilityListAbortRef.current.abort();
    }
    const ac = new AbortController();
    availabilityListAbortRef.current = ac;
    // در حال بارگذاری لیست، پاسخ جزئیات تایم‌لاین نباید با لیست قاطی شود
    if (availabilityByIdAbortRef.current) {
      availabilityByIdAbortRef.current.abort();
    }

    setAvailabilityTableLoading(false);
    setLoading(true);
    // Axis window should be recalculated for each availability fetch.
    setAxisWindow(null);

    const off = dayOffsetRef.current;
    getAvailabilityHistory({
      ssl_id: selected?.SSL?.value,
      spec_id: selected?.Size?.value,
      terminal_id: selected?.Terminal?.value || undefined,
      day_offset: off > 0 ? off : undefined,
      signal: ac.signal,
    })
      .then((res) => {
        if (ac.signal.aborted) return;

        const r = pickAvailabilityHistoryPayloadRoot(res);
        const nowObj =
          r?.now != null && typeof r.now === 'object' && !Array.isArray(r.now)
            ? r.now
            : null;

        // همیشه اول `now` — همان چیزی که در Network برای وضعیت لحظه‌ای می‌بینید
        const availability =
          nowObj?.availability ??
          r?.availability ??
          r?.data?.availability ??
          r?.availability_now ??
          null;

        const terminals =
          nowObj?.terminals ??
          r?.terminals ??
          r?.data?.terminals ??
          r?.terminals_now ??
          null;

        /** فیلتر Terminal: همیشه از همان `now.terminals` که با جدول هم‌خوان است */
        const terminalFilterOptions =
          buildTerminalFilterOptionsFromNowTerminals(terminals);
        setTerminalOptions(terminalFilterOptions);
        setSelected((prev) => {
          const v = prev?.Terminal?.value;
          if (v == null) return prev;
          const ok = terminalFilterOptions.some(
            (o) => o?.value != null && Number(o.value) === Number(v)
          );
          if (ok) return prev;
          return {
            ...prev,
            Terminal: { label: 'All Terminals', value: null },
          };
        });

        const activeShift =
          nowObj?.active_shift ??
          r?.active_shift ??
          r?.data?.active_shift ??
          'shift1';

        const startDate =
          nowObj?.start_date ?? r?.start_date ?? r?.data?.start_date ?? null;

        const slots = nowObj?.slots ?? r?.slots ?? r?.data?.slots ?? null;

        const subtitleSource = nowObj ?? r;
        const subtitleMerged = {
          ...(r || {}),
          ...(subtitleSource && typeof subtitleSource === 'object'
            ? subtitleSource
            : {}),
        };

        const availabilityHistories =
          r?.availabilityHistories ?? r?.availability_histories ?? null;

        if (availability && terminals) {
          const mapped = mapAvailabilityToAppointmentData(
            availability,
            terminals
          );
          setAppointmentData(mapped);
          setShift((activeShift || 'shift1') === 'shift1');
          setStartDate(startDate);
          const subtitle = buildLast24HoursSubtitle(subtitleMerged);
          setTableSubTitle(subtitle);

          // If backend includes "now" slots, show banner immediately.
          const nowHistory = Array.isArray(slots)
            ? (() => {
                const merged = { ...(subtitleSource || {}), slots };
                // id رکورد لیست ≠ اسنپ‌شات «now»؛ باعث فیلتر اشتباه ترمینال و مودال خالی می‌شد.
                delete merged.id;
                delete merged.history_id;
                return merged;
              })()
            : null;
          setCurrentHistory(nowHistory);

          // Keep an easy way to reset UI (when user clicks "Clear").
          nowSnapshotRef.current = {
            appointmentData: mapped,
            shift: (activeShift || 'shift1') === 'shift1',
            startDate,
            tableSubTitle: subtitle,
            currentHistory: nowHistory,
          };
          // هر fetch جدید = نمایش «now» روی جدول، نه آیتم انتخاب‌شدهٔ قبلی تایم‌لاین.
          setSelectedHistoryId(null);
          setHoveredHistory(null);
        } else {
          setAppointmentData([]);
          setCurrentHistory(null);
          setStartDate(null);
          setTableSubTitle('');
          nowSnapshotRef.current = null;
          setSelectedHistoryId(null);
        }

        // Normalization: ما در render انتظار داریم histories?.data آرایه باشد.
        if (Array.isArray(availabilityHistories)) {
          setHistories({ data: availabilityHistories });
        } else if (
          availabilityHistories &&
          Array.isArray(availabilityHistories.data)
        ) {
          setHistories(availabilityHistories);
        } else {
          setHistories(availabilityHistories || null);
        }
      })
      .catch((err) => {
        if (ac.signal.aborted || isRequestAbortedError(err)) return;
        SaveApiResMessage({
          title: 'Error',
          message: err,
          mode: 'warning',
        });
      })
      .finally(() => {
        if (!ac.signal.aborted) {
          setLoading(false);
        }
      });
  };

  const goPrevDay = () => {
    if (dayOffsetRef.current >= MAX_AVAIL_HISTORY_DAY_OFFSET) return;
    dayOffsetRef.current += 1;
    setDayOffset(dayOffsetRef.current);
    fetchAvailability();
  };

  const goNextDay = () => {
    if (dayOffsetRef.current <= 0) return;
    dayOffsetRef.current -= 1;
    setDayOffset(dayOffsetRef.current);
    fetchAvailability();
  };

  const handleSelectHistory = (history) => {
    if (!history?.id) return;

    if (availabilityByIdAbortRef.current) {
      availabilityByIdAbortRef.current.abort();
    }
    const ac = new AbortController();
    availabilityByIdAbortRef.current = ac;
    setAvailabilityTableLoading(true);
    getAvailabilityHistoryById(history.id, { signal: ac.signal })
      .then((res) => {
        if (ac.signal.aborted) return;
        const data = pickHistoryByIdPayloadRoot(res);

        const availability = data?.availability ?? res?.availability ?? null;
        const terminals = data?.terminals ?? res?.terminals ?? null;

        setAppointmentData(
          mapAvailabilityToAppointmentData(availability, terminals)
        );

        const activeShift =
          data?.active_shift ??
          data?.activeShift ??
          res?.active_shift ??
          res?.activeShift ??
          'shift1';

        setShift((activeShift || 'shift1') === 'shift1');
        setStartDate(data?.start_date ?? null);
        setCurrentHistory(data);
        setSelectedHistoryId(history.id);
      })
      .catch((err) => {
        if (ac.signal.aborted || isRequestAbortedError(err)) return;
        SaveApiResMessage({
          title: 'Error',
          message: err,
          mode: 'warning',
        });
      })
      .finally(() => {
        if (!ac.signal.aborted) {
          setAvailabilityTableLoading(false);
        }
      });
  };

  /** برگرداندن جدول و اسنپ‌شات مودال به همان payload «now»ی که از getAvailabilityHistory آمده (نه آیتم تایم‌لاین). */
  const handleClearSnapshot = () => {
    const snap = nowSnapshotRef.current;
    if (!snap) return;
    setAppointmentData(snap.appointmentData || []);
    setShift(!!snap.shift);
    setStartDate(snap.startDate || null);
    if (snap.tableSubTitle) setTableSubTitle(snap.tableSubTitle);
    setCurrentHistory(snap.currentHistory ?? null);
    setSelectedHistoryId(null);
    setHoveredHistory(null);
    setAvailabilityTableLoading(false);

    /** بعد از برگشت به now، تایم‌لاین کاملاً به راست (نقطهٔ now) اسکرول شود — همان `scrollTimelineToEnd` */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = timelineInnerScrollRef.current;
        if (!el) return;
        const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
        el.scrollLeft = maxScroll;
        refreshTimelineScrollEdges();
      });
    });
  };

  /** کلیک روی دایرهٔ now: لغو fetch جزئیات تاریخچه + بازگشت به دادهٔ now (همان Clear). */
  const handleSelectNowSnapshot = () => {
    if (availabilityByIdAbortRef.current) {
      availabilityByIdAbortRef.current.abort();
      availabilityByIdAbortRef.current = null;
    }
    handleClearSnapshot();
  };

  const extractTimeLabel = (slotValue) => {
    const s = slotValue == null ? '' : String(slotValue);
    // Expected formats like "03/16 22:00" or just "... 22:00"
    const m = s.match(/(\d{1,2}:\d{2})/);
    return m ? m[1] : '';
  };

  const extractDateShortLabel = (slotValue) => {
    const s = slotValue == null ? '' : String(slotValue);
    // Example: "Thursday 03/19/2026 09:30 - 10:30"
    const m = s.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (!m) return '';
    const mm = String(m[1]).padStart(2, '0');
    const dd = String(m[2]).padStart(2, '0');
    return `${mm}/${dd}`;
  };

  const extractDateTimeShortLabel = (slotValue) => {
    const date = extractDateShortLabel(slotValue);
    const time = extractTimeLabel(slotValue);
    if (date && time) return `${date} ${time}`;
    return date || time || '';
  };

  /**
   * API timestamps like "2026-03-18T20:05:08.000000Z" are UTC.
   * Parse as Date and format with local getMonth/getDate/getHours → user's local time.
   */
  const formatCreatedAtLocalLabel = (historyOrIso) => {
    const raw =
      historyOrIso == null
        ? null
        : typeof historyOrIso === 'string' || historyOrIso instanceof Date
        ? historyOrIso
        : historyOrIso?.created_at ??
          historyOrIso?.createdAt ??
          historyOrIso?.updated_at ??
          historyOrIso?.updatedAt ??
          null;
    if (raw == null || raw === '') return '';
    if (raw instanceof Date) {
      if (isNaN(raw.getTime())) return '';
      const d = raw;
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const hh = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      return `${mm}/${dd} ${hh}:${min}`;
    }
    const fixed = String(raw).split('.')[0].replace(' ', 'T');
    const d = new Date(fixed.includes('Z') ? fixed : `${fixed}Z`);
    if (isNaN(d.getTime())) return '';
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${mm}/${dd} ${hh}:${min}`;
  };

  const getTerminalsCheckedCount = (history) => {
    const direct =
      history?.terminalsChecked ??
      history?.terminals_checked ??
      history?.terminalCount ??
      history?.terminal_count ??
      null;
    if (typeof direct === 'number') return direct;

    if (Array.isArray(history?.terminals)) return history.terminals.length;

    // Terminals can be an object keyed by id/slug.
    if (history?.terminals && typeof history.terminals === 'object') {
      return Object.keys(history.terminals).length;
    }

    // Fallback: if backend doesn't provide a direct count number,
    // derive it from the checked terminals list.
    const list = getCheckedTerminalsList(history);
    if (Array.isArray(list) && list.length) return list.length;

    return null;
  };

  const getCheckedTerminalsList = (history) => {
    const list =
      history?.terminalsCheckedList ??
      history?.terminals_checked_list ??
      history?.terminalsChecked ??
      history?.terminals_checked ??
      history?.checked_terminals ??
      history?.checkedTerminals ??
      history?.terminals ??
      null;

    if (Array.isArray(list)) return list;
    if (list && typeof list === 'object') return Object.values(list);
    return [];
  };

  const formatHoursMinutesAgo = (updatedAt) => {
    if (!updatedAt) return '';

    // Backend sometimes returns "...." with milliseconds, so normalize.
    const fixedUpdatedAt = String(updatedAt).split('.')[0] + 'Z';
    const updatedDate = new Date(fixedUpdatedAt);
    if (isNaN(updatedDate.getTime())) return '';

    const diffMs = Date.now() - updatedDate.getTime();
    if (diffMs < 0) return 'Just now';

    const totalMinutes = Math.floor(diffMs / 60000);
    const days = Math.floor(totalMinutes / (24 * 60));
    const remainingMinutesAfterDays = totalMinutes - days * 24 * 60;
    const hours = Math.floor(remainingMinutesAfterDays / 60);
    const minutes = remainingMinutesAfterDays % 60;

    // Match UX requirements:
    // - <24h  => "Xh Ym ago" or "Ym ago"
    // - >=24h => "Xd Xh Ym ago" (days should appear too)
    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m ago`;
    }
    if (hours > 0) return `${hours}h ${minutes}m ago`;
    return `${minutes}m ago`;
  };

  const getHistoryTimestampMs = (history) => {
    // For cases where backend only gives time (HH:mm) without date,
    // we need a consistent "last 24h window" to decide today vs yesterday.
    const endLocal = new Date();
    endLocal.setMinutes(0, 0, 0);
    const windowEndMs = endLocal.getTime();
    const windowStartMs = windowEndMs - 24 * 3600 * 1000;

    const raw =
      history?.created_at ??
      history?.createdAt ??
      history?.updated_at ??
      history?.updatedAt ??
      history?.timestamp ??
      null;

    if (raw) {
      const fixed = String(raw).includes('Z')
        ? String(raw)
        : String(raw).split('.')[0] + 'Z';
      const d = new Date(fixed);
      if (!isNaN(d.getTime())) return d.getTime();
    }

    const slots = Array.isArray(history?.slots) ? history.slots : [];
    const slotToText = (s) =>
      s == null ? '' : typeof s === 'string' ? s : String(s);

    // Expected: "MM/DD HH:mm" like "03/17 22:00" (tolerant for 1-2 digit parts)
    const dateTimeRegex = /(\d{1,2})[\/-](\d{1,2})\s*,?\s*(\d{1,2}):(\d{2})/;
    const timeOnlyRegex = /(\d{1,2}):(\d{2})/;

    const slotWithDateTime = slots.find((s) =>
      dateTimeRegex.test(slotToText(s))
    );

    if (slotWithDateTime) {
      const m = slotToText(slotWithDateTime).match(dateTimeRegex);
      if (m) {
        const mm = Number(m[1]);
        const dd = Number(m[2]);
        const hh = Number(m[3]);
        const min = Number(m[4]);

        // سال تاریخ از طرف API همیشه کامل نیست (slot فقط MM/DD HH:mm دارد)،
        // بنابراین برای جلوگیری از جابه‌جایی محور با انتخاب یک snapshot،
        // سال را ثابت از «الان» می‌گیریم.
        const year = new Date().getFullYear();
        const d = new Date(year, mm - 1, dd, hh, min, 0, 0);
        if (!isNaN(d.getTime())) return d.getTime();
      }
    }

    // Fallback: time-only, like "... 3:01" or "... 03:01"
    // Use endLocal's date, otherwise try yesterday (based on the axis 24h window).
    const slotWithTimeOnly = slots.find((s) =>
      timeOnlyRegex.test(slotToText(s))
    );

    if (slotWithTimeOnly) {
      const timeOnlyMatch = slotToText(slotWithTimeOnly).match(timeOnlyRegex);
      if (timeOnlyMatch) {
        const hh = Number(timeOnlyMatch[1]);
        const min = Number(timeOnlyMatch[2]);
        if (!isNaN(hh) && !isNaN(min)) {
          const dToday = new Date(
            endLocal.getFullYear(),
            endLocal.getMonth(),
            endLocal.getDate(),
            hh,
            min,
            0,
            0
          );
          const dYesterday = new Date(dToday.getTime() - 24 * 3600 * 1000);

          const tA = dToday.getTime();
          const tB = dYesterday.getTime();

          const inWindowA = tA >= windowStartMs && tA <= windowEndMs;
          if (inWindowA && !isNaN(tA)) return tA;

          const inWindowB = tB >= windowStartMs && tB <= windowEndMs;
          if (inWindowB && !isNaN(tB)) return tB;
        }
      }
    }

    return null;
  };

  const orderedHistories = useMemo(() => {
    const list = histories?.data;
    if (!Array.isArray(list)) return [];

    const withTs = list
      .map((x) => ({ x, ts: getHistoryTimestampMs(x) }))
      .filter((o) => !!o.x);

    // Sort oldest -> newest
    withTs.sort((a, b) => {
      const ta = a.ts;
      const tb = b.ts;
      if (ta == null && tb == null) return 0;
      if (ta == null) return 1;
      if (tb == null) return -1;
      return ta - tb;
    });

    return withTs.map((o) => o.x);
  }, [histories?.data]);

  const historyById = useMemo(() => {
    const map = new Map();
    orderedHistories.forEach((h) => {
      if (h?.id != null) map.set(String(h.id), h);
    });
    return map;
  }, [orderedHistories]);

  useEffect(() => {
    if (loading) return;

    const list = histories?.data;
    if (!Array.isArray(list) || list.length === 0) {
      setAxisWindow(null);
      return;
    }

    const tsList = list
      .map((it) => getHistoryTimestampMs(it))
      .filter((t) => typeof t === 'number' && !Number.isNaN(t));

    if (!tsList.length) {
      setAxisWindow(null);
      return;
    }

    const H24_MS = 24 * 3600 * 1000;
    const tryParseStartDateLocalMs = (s) => {
      if (!s || typeof s !== 'string') return null;
      // Format: "YYYY-MM-DD HH:mm:ss"
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/);
      if (!m) return null;
      const year = Number(m[1]);
      const month = Number(m[2]);
      const day = Number(m[3]);
      const hh = Number(m[4]);
      const mm = Number(m[5]);
      const ss = Number(m[6]);
      return new Date(year, month - 1, day, hh, mm, ss, 0).getTime();
    };

    const tryParseStartDateUtcMs = (s) => {
      if (!s || typeof s !== 'string') return null;
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/);
      if (!m) return null;
      const year = Number(m[1]);
      const month = Number(m[2]);
      const day = Number(m[3]);
      const hh = Number(m[4]);
      const mm = Number(m[5]);
      const ss = Number(m[6]);
      return Date.UTC(year, month - 1, day, hh, mm, ss, 0);
    };

    const scoreWindow = (startMs) => {
      const endMs = startMs + H24_MS;
      const score = tsList.filter((t) => t >= startMs && t <= endMs).length;
      return { startMs, endMs, score };
    };

    const latestTs = Math.max(...tsList);
    const endLocal = new Date(latestTs);
    endLocal.setMinutes(0, 0, 0); // align to local hour boundary
    const endLocalMs = endLocal.getTime();
    const latestWindow = scoreWindow(endLocalMs - H24_MS);
    latestWindow.source = 'latestTsLocalHour';

    const candidates = [latestWindow];

    const parsedLocalStart = tryParseStartDateLocalMs(startDate);
    if (parsedLocalStart != null) {
      const c = scoreWindow(parsedLocalStart);
      c.source = 'backendStartDateLocal';
      candidates.push(c);
    }

    const parsedUtcStart = tryParseStartDateUtcMs(startDate);
    if (parsedUtcStart != null && parsedUtcStart !== parsedLocalStart) {
      const c = scoreWindow(parsedUtcStart);
      c.source = 'backendStartDateUtc';
      candidates.push(c);
    }

    candidates.sort((a, b) => b.score - a.score);
    const best = candidates[0];
    if (!best) return;

    setAxisWindow({
      startMs: best.startMs,
      endMs: best.endMs,
      source: best.source,
    });
  }, [loading, histories?.data, startDate]);

  // After API loading remounts/unmounts the timeline,
  // hover events might not re-fire. We re-check what's under the mouse
  // and open/close tooltip accordingly.
  useEffect(() => {
    if (loading) return;

    const { x, y } = mouseClientPosRef.current;
    if (!x && !y) return;

    const el = document.elementFromPoint(x, y);
    const btn = el?.closest?.('[data-avail-history-id]');

    if (!btn) {
      setHoveredHistory(null);
      return;
    }

    const btnId = btn.getAttribute('data-avail-history-id');
    if (btnId === AVAIL_HISTORY_NOW_DOT_ID) {
      const snap = nowSnapshotRef.current?.currentHistory;
      if (!snap) {
        setHoveredHistory(null);
        return;
      }
      const rect = btn.getBoundingClientRect();
      setHoverTooltipPos({
        top: rect.top - 8,
        left: rect.left + rect.width / 2,
      });
      setHoveredHistory({ ...snap, __availNowDot: true });
      return;
    }

    const item = historyById.get(String(btnId));

    if (!item) {
      setHoveredHistory(null);
      return;
    }

    const rect = btn.getBoundingClientRect();
    setHoverTooltipPos({
      top: rect.top - 8,
      left: rect.left + rect.width / 2,
    });
    setHoveredHistory(item);
  }, [loading, historyById]);

  const TIMELINE_PADDING_PX = 12;
  const MIN_CIRCLE_CENTER_DISTANCE_PX = 70;
  const MAX_TIMELINE_INNER_WIDTH_PX = 2600;
  const TIMELINE_HOURS = 24;
  const TIMELINE_BUTTON_CIRCLE_SIZE_PX = 15;
  const TIMELINE_BUTTON_MIN_GAP_PX = Math.max(
    2,
    Math.round(TIMELINE_BUTTON_CIRCLE_SIZE_PX * 0.2)
  );
  const TIMELINE_BUTTON_CENTER_SPACING_PX =
    TIMELINE_BUTTON_CIRCLE_SIZE_PX + TIMELINE_BUTTON_MIN_GAP_PX;
  const TIMELINE_TRACK_CENTER_Y_PX = 85;
  const TIMELINE_TRACK_LINE_HEIGHT_PX = 2;
  const TIMELINE_TICK_HEIGHT_PX = 6;
  const TIMELINE_TRACK_LINE_TOP_PX =
    TIMELINE_TRACK_CENTER_Y_PX - TIMELINE_TRACK_LINE_HEIGHT_PX / 2;
  const TIMELINE_TICK_TOP_PX =
    TIMELINE_TRACK_CENTER_Y_PX - TIMELINE_TICK_HEIGHT_PX / 2;
  const timelinePValues = useMemo(() => {
    const n = orderedHistories.length;
    if (!n) return [];

    const windowEndMs =
      axisWindow?.endMs ??
      (() => {
        const endLocal = new Date();
        endLocal.setMinutes(0, 0, 0);
        return endLocal.getTime();
      })();
    const windowStartMs =
      axisWindow?.startMs ?? windowEndMs - TIMELINE_HOURS * 3600 * 1000;

    return orderedHistories.map((item, idx) => {
      const ts = getHistoryTimestampMs(item);

      if (ts == null) {
        if (n === 1) return 0.5;
        return idx / (n - 1); // 0..1 across the line
      }

      const p = (ts - windowStartMs) / (windowEndMs - windowStartMs); // 0..1
      return Math.max(0, Math.min(1, p));
    });
  }, [orderedHistories, axisWindow]);

  const hourGroups = useMemo(() => {
    const groups = Array.from({ length: TIMELINE_HOURS }, () => []);
    timelinePValues.forEach((p, idx) => {
      const hourIndex = Math.max(
        0,
        Math.min(TIMELINE_HOURS - 1, Math.floor(p * TIMELINE_HOURS))
      );
      groups[hourIndex].push(idx);
    });
    return groups;
  }, [timelinePValues]);

  const timelineNowPlacement = useMemo(() => {
    const windowEndMs =
      axisWindow?.endMs ??
      (() => {
        const endLocal = new Date();
        endLocal.setMinutes(0, 0, 0);
        return endLocal.getTime();
      })();
    const windowStartMs =
      axisWindow?.startMs ?? windowEndMs - TIMELINE_HOURS * 3600 * 1000;
    const span = windowEndMs - windowStartMs;
    const pNow = !(span > 0)
      ? 0.5
      : Math.max(0, Math.min(1, (Date.now() - windowStartMs) / span));
    const nowHourIndex = Math.max(
      0,
      Math.min(TIMELINE_HOURS - 1, Math.floor(pNow * TIMELINE_HOURS))
    );
    return { pNow, nowHourIndex };
  }, [axisWindow]);

  const showNowOnTimeline = dayOffset === 0;

  const maxCirclesInAnyHour = useMemo(() => {
    const { nowHourIndex } = timelineNowPlacement;
    return hourGroups.reduce(
      (max, g, hourIndex) =>
        Math.max(
          max,
          g.length + (showNowOnTimeline && hourIndex === nowHourIndex ? 1 : 0)
        ),
      0
    );
  }, [hourGroups, timelineNowPlacement, showNowOnTimeline]);

  const pixelsPerHourPx = useMemo(() => {
    const mMax = maxCirclesInAnyHour || 1;
    return (
      mMax * TIMELINE_BUTTON_CIRCLE_SIZE_PX +
      Math.max(0, mMax - 1) * TIMELINE_BUTTON_MIN_GAP_PX
    );
  }, [
    maxCirclesInAnyHour,
    TIMELINE_BUTTON_CIRCLE_SIZE_PX,
    TIMELINE_BUTTON_MIN_GAP_PX,
  ]);

  const timelineTrackWidthPx = TIMELINE_HOURS * pixelsPerHourPx;

  const timelineInnerWidth = useMemo(() => {
    return TIMELINE_PADDING_PX * 2 + timelineTrackWidthPx;
  }, [timelineTrackWidthPx]);

  const timelinePositions = useMemo(() => {
    return timelinePValues.map(
      (p) => TIMELINE_PADDING_PX + p * timelineTrackWidthPx
    );
  }, [timelinePValues, timelineTrackWidthPx]);

  const timelineStepPx = pixelsPerHourPx;

  const TIMELINE_BASE_HEIGHT_PX = Math.round(
    98 + TIMELINE_BUTTON_CIRCLE_SIZE_PX * 2.4
  );
  const TIMELINE_BUTTON_BASE_TOP_PX =
    TIMELINE_TRACK_CENTER_Y_PX - TIMELINE_BUTTON_CIRCLE_SIZE_PX / 2;

  const { timelineLayout, nowDotCenterX } = useMemo(() => {
    const layout = new Array(timelinePValues.length);
    const { pNow, nowHourIndex } = timelineNowPlacement;
    const trackW = TIMELINE_HOURS * pixelsPerHourPx;
    let nowX = TIMELINE_PADDING_PX + pNow * trackW;

    hourGroups.forEach((indices, hourIndex) => {
      const items = indices.map((idx) => ({ idx, p: timelinePValues[idx] }));
      if (showNowOnTimeline && hourIndex === nowHourIndex) {
        items.push({ idx: null, p: pNow, isNow: true });
      }
      if (items.length === 0) return;

      const m = items.length;
      const baseX = TIMELINE_PADDING_PX + (hourIndex + 0.5) * pixelsPerHourPx;
      const circleRadius = TIMELINE_BUTTON_CIRCLE_SIZE_PX / 2;
      const segLeft = baseX - pixelsPerHourPx / 2 + circleRadius;
      const segRight = baseX + pixelsPerHourPx / 2 - circleRadius;

      const sorted = [...items].sort((a, b) => a.p - b.p);

      const step = TIMELINE_BUTTON_CENTER_SPACING_PX;
      const midIndex = Math.ceil((m - 1) / 2);
      const firstCenter = baseX - step * midIndex;

      sorted.forEach((item, k) => {
        const rawX = firstCenter + step * k;
        const x = Math.min(segRight, Math.max(segLeft, rawX));
        if (item.isNow) {
          nowX = x;
        } else {
          layout[item.idx] = { x, size: TIMELINE_BUTTON_CIRCLE_SIZE_PX };
        }
      });
    });

    return { timelineLayout: layout, nowDotCenterX: nowX };
  }, [
    timelinePValues,
    hourGroups,
    pixelsPerHourPx,
    timelineNowPlacement,
    TIMELINE_BUTTON_CENTER_SPACING_PX,
    TIMELINE_BUTTON_CIRCLE_SIZE_PX,
    showNowOnTimeline,
  ]);

  const timelineInnerHeight = useMemo(() => {
    return TIMELINE_BASE_HEIGHT_PX;
  }, []);

  const scrollTimeline = (dir) => {
    const el = timelineInnerScrollRef.current;
    if (!el || timelineStepPx <= 0) return;
    el.scrollBy({ left: dir * timelineStepPx, behavior: 'smooth' });
  };

  const scrollTimelineToEnd = useCallback(() => {
    const el = timelineInnerScrollRef.current;
    if (!el) return;
    const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
    el.scrollLeft = maxScroll;
    refreshTimelineScrollEdges();
  }, [refreshTimelineScrollEdges]);

  useLayoutEffect(() => {
    if (loading || !histories) return;
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollTimelineToEnd();
      });
    });
    return () => cancelAnimationFrame(id);
  }, [loading, histories, orderedHistories.length, scrollTimelineToEnd]);

  useLayoutEffect(() => {
    const id = requestAnimationFrame(() => refreshTimelineScrollEdges());
    return () => cancelAnimationFrame(id);
  }, [
    orderedHistories.length,
    timelineTrackWidthPx,
    pixelsPerHourPx,
    loading,
    refreshTimelineScrollEdges,
  ]);

  useEffect(() => {
    window.addEventListener('resize', refreshTimelineScrollEdges);
    return () =>
      window.removeEventListener('resize', refreshTimelineScrollEdges);
  }, [refreshTimelineScrollEdges]);

  const availHistoryGetSnapshotAvailabilities = useCallback(
    (rowTerminalId) => {
      const h = currentHistory;
      if (!h || !Array.isArray(h.slots) || h.slots.length === 0) {
        return [];
      }
      const historyTerminalId =
        h.terminal_id ?? h.terminal?.id ?? h.terminalId ?? null;

      const shouldMatchTerminalRow = (() => {
        if (h?.id == null) return false;
        if (historyTerminalId == null || rowTerminalId == null) return false;

        const tc =
          h?.terminalsChecked ??
          h?.terminals_checked ??
          h?.terminalCount ??
          h?.terminal_count ??
          null;
        if (typeof tc === 'number' && tc === 1) return true;

        if (Array.isArray(h?.terminals) && h.terminals.length === 1)
          return true;

        if (
          h?.terminals &&
          typeof h.terminals === 'object' &&
          !Array.isArray(h.terminals)
        ) {
          const keys = Object.keys(h.terminals);
          if (keys.length === 1) return true;
        }

        return false;
      })();

      if (
        shouldMatchTerminalRow &&
        Number(rowTerminalId) !== Number(historyTerminalId)
      ) {
        return [];
      }

      const rows = [];
      h.slots.forEach((raw, i) => {
        const text = snapshotSlotDisplayText(raw);
        if (text) {
          rows.push({
            id: `avail-history-snap-${h.id ?? 'x'}-${i}`,
            text,
          });
        }
      });
      return rows;
    },
    [currentHistory]
  );

  const openAvailabilitySnapshotModal = useCallback((payload) => {
    setAvailabilitySnapshotModal(payload);
  }, []);

  useEffect(() => {
    if (selectedHistoryId == null) {
      setAvailabilitySnapshotModal(null);
    }
  }, [selectedHistoryId]);

  const availHistoryRestrictClicksToTerminalId = useMemo(() => {
    if (selectedHistoryId == null) return null;
    const h = currentHistory;
    if (!h || typeof h !== 'object') return null;
    const tid = h.terminal_id ?? h.terminal?.id ?? h.terminalId ?? null;
    if (tid == null) return null;
    return Number(tid);
  }, [selectedHistoryId, currentHistory]);

  const renderHistoryHoverTooltip = () => {
    if (!hoveredHistory) return null;

    const slotsLen = Array.isArray(hoveredHistory?.slots)
      ? hoveredHistory.slots.length
      : 0;

    const topDateTimeLabel = formatCreatedAtLocalLabel(hoveredHistory);

    const historyTerminalId =
      hoveredHistory?.terminal_id ?? hoveredHistory?.terminal?.id ?? null;

    const terminalLabelFromLookup = (id) => {
      if (id == null) return '';
      const found = terminalOptions.find(
        (o) => o?.value != null && String(o.value) === String(id)
      );
      return found?.label || '';
    };

    const mainTerminalTitle =
      terminalLabelFromLookup(historyTerminalId) ||
      hoveredHistory?.terminal?.name ||
      hoveredHistory?.terminal?.slug ||
      hoveredHistory?.terminal_name ||
      '';

    const terminalsCheckedCount = getTerminalsCheckedCount(hoveredHistory);

    const ago =
      formatHoursMinutesAgo(
        hoveredHistory?.created_at ||
          hoveredHistory?.createdAt ||
          hoveredHistory?.updated_at ||
          hoveredHistory?.updatedAt
      ) || '';

    const metaParts = [];
    if (typeof terminalsCheckedCount === 'number') {
      metaParts.push(`${terminalsCheckedCount} terminals scanned`);
    }
    if (ago) metaParts.push(ago);

    return (
      <div
        ref={tooltipRef}
        style={{
          position: 'fixed',
          zIndex: VERY_HIGH_ZINDEX,
          top: hoverTooltipPos.top,
          left: hoverTooltipPos.left,
          transform: 'translate(-50%, -100%)',
          width: 200,
          maxWidth: 'min(200px, calc(100vw - 24px))',
          background: '#ffffff',
          borderRadius: 10,
          boxShadow: '0 10px 25px rgba(0,0,0,0.12)',
          border: '1px solid rgba(0,0,0,0.06)',
          padding: 14,
          pointerEvents: 'none',
          fontFamily: 'Averta',
          color: '#2b303b',
          boxSizing: 'border-box',
        }}>
        <div
          dir="ltr"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
          }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 500,
              color: '#8B93A6',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: 1,
              minWidth: 0,
              textAlign: 'left',
            }}>
            {topDateTimeLabel || '—'}
          </div>
          <div
            style={{
              background: '#e7fbf0',
              color: '#1FC16B',
              borderRadius: 999,
              padding: '4px 8px',
              fontSize: 11,
              fontWeight: 700,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}>
            {slotsLen} slots
          </div>
        </div>

        {mainTerminalTitle ? (
          <div
            style={{
              marginTop: 8,
              fontSize: 13,
              fontWeight: 700,
              color: '#2b303b',
              lineHeight: 1.25,
              wordBreak: 'break-word',
            }}>
            {mainTerminalTitle}
          </div>
        ) : null}

        {/* scanned + relative time */}
        <div
          style={{
            marginTop: 6,
            fontSize: 12,
            fontWeight: 500,
            color: '#8B93A6',
            lineHeight: 1.35,
          }}>
          {metaParts.length ? (
            <>
              {metaParts[0]}
              {metaParts[1] ? (
                <>
                  {' '}
                  <span style={{ color: '#8B93A6' }}>•</span> {metaParts[1]}
                </>
              ) : null}
            </>
          ) : (
            '—'
          )}
        </div>
      </div>
    );
  };

  useLayoutEffect(() => {
    if (!hoveredHistory) return;
    const el = tooltipRef.current;
    if (!el) return;

    const padding = 8;
    const box = el.getBoundingClientRect();
    const tooltipWidth = box.width || 200;
    const tooltipHeight = box.height || 160;

    const leftMin = padding + tooltipWidth / 2;
    const leftMax = window.innerWidth - padding - tooltipWidth / 2;

    const topMin = padding + tooltipHeight; // because transform(-100%) makes tooltip go up by its height
    const topMax = window.innerHeight - padding;

    const effectiveLeftMin = Math.min(leftMin, leftMax);
    const effectiveTopMin = Math.min(topMin, topMax);

    const clampedLeft = Math.min(
      leftMax,
      Math.max(effectiveLeftMin, hoverTooltipPos.left)
    );
    const clampedTop = Math.min(
      topMax,
      Math.max(effectiveTopMin, hoverTooltipPos.top)
    );

    if (
      clampedLeft !== hoverTooltipPos.left ||
      clampedTop !== hoverTooltipPos.top
    ) {
      setHoverTooltipPos({ top: clampedTop, left: clampedLeft });
    }
  }, [hoveredHistory, hoverTooltipPos.left, hoverTooltipPos.top]);

  useEffect(() => {
    loadLookups();
  }, []);

  useEffect(() => {
    if (!selected?.SSL?.value || !selected?.Size?.value) return;
    dayOffsetRef.current = 0;
    setDayOffset(0);
    fetchAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.SSL?.value, selected?.Size?.value, selected?.Terminal?.value]);

  useEffect(() => {
    // Fallback: if backend doesn't provide last24 subtitle fields,
    // compute it from histories.data (so UI always shows like Figma).
    if (!tableSubTitle && histories) {
      const computed = computeLast24HoursSubtitleFromHistories(histories);
      if (computed) setTableSubTitle(computed);
    }
  }, [histories, tableSubTitle]);

  const timelineSubtitleLine = useMemo(() => {
    const base = (tableSubTitle || '').trim();
    if (dayOffset <= 0) return base;

    const windowLabel = dayOffset === 1 ? '1 day ago' : `${dayOffset} days ago`;

    if (/^Last 24 hours/i.test(base)) {
      return base.replace(/^Last 24 hours/i, windowLabel);
    }

    if (base) {
      return `${windowLabel} · ${base}`;
    }
    return windowLabel;
  }, [tableSubTitle, dayOffset]);

  return (
    <div style={{ overflowX: 'hidden' }}>
      <div
        className={cls.card}
        style={{
          padding: 0,
          margin: 0,
          marginRight: 0,
          boxShadow: 'none',
          border: '1px solid #e1e4ea',
          borderRadius: 4,
          background: '#fff',
          overflow: 'hidden',
        }}>
        <div style={{ padding: '12px 12px' }}>
          <CustomGrid>
            <CustomGridItem xs={2} sm={2} md={2}>
              <CustomSelectEx
                dropdown={true}
                title="Size"
                placeholder="Size"
                value={selected?.Size}
                onChange={(item) =>
                  setSelected((prev) => ({ ...prev, Size: item }))
                }
                options={sizeOptions}
              />
            </CustomGridItem>

            <CustomGridItem xs={2} sm={2} md={2}>
              <CustomSelectEx
                dropdown={true}
                title="SSL"
                placeholder="SSL"
                value={selected?.SSL}
                onChange={(item) =>
                  setSelected((prev) => ({ ...prev, SSL: item }))
                }
                options={SSLOptions}
              />
            </CustomGridItem>

            <CustomGridItem xs={2} sm={2} md={2}>
              <CustomSelectEx
                dropdown={true}
                title="Terminal"
                placeholder="All Terminals"
                value={selected?.Terminal}
                onChange={(item) =>
                  setSelected((prev) => ({ ...prev, Terminal: item }))
                }
                options={terminalOptions}
              />
            </CustomGridItem>

            <CustomGridItem xs={6} sm={6} md={6}></CustomGridItem>
          </CustomGrid>
        </div>
      </div>

      {loading ? (
        <div className="mt-16">
          <LoadingV2 />
        </div>
      ) : (
        <>
          <div
            style={{
              marginTop: 12,
              border: '1px solid #e1e4ea',
              borderRadius: 4,
              background: '#fff',
              padding: '12px 12px 10px 12px',
            }}>
            {/* Top info line (Figma: "Last 24 hours ...") */}
            {timelineSubtitleLine ||
            (selected?.SSL?.value && selected?.Size?.value) ? (
              <div
                style={{
                  marginTop: 0,
                  fontSize: 12,
                  fontFamily: 'Averta',
                  color: '#2b303b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 10,
                  flexWrap: 'wrap',
                  padding: '0 6px',
                }}>
                <span
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flex: '1 1 120px',
                    minWidth: 0,
                  }}>
                  {timelineSubtitleLine || '\u00a0'}
                </span>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    flexShrink: 0,
                    flexWrap: 'wrap',
                  }}>
                  <div className={tableNavStyles.buttonContainer}>
                    <button
                      type="button"
                      disabled={dayOffset >= MAX_AVAIL_HISTORY_DAY_OFFSET}
                      className={`${timelineNavStyles.timelineNavBtn} ${timelineNavStyles.timelineNavBtnDay}`}
                      onClick={goPrevDay}>
                      <ReactSVG
                        src={Icons.leftArrow}
                        className={timelineNavStyles.timelineNavArrow}
                        beforeInjection={(svg) => {
                          svg.querySelectorAll('path').forEach((path) => {
                            path.setAttribute(
                              'stroke',
                              dayOffset >= MAX_AVAIL_HISTORY_DAY_OFFSET
                                ? '#CACFD8'
                                : '#131517'
                            );
                          });
                        }}
                      />
                      <span
                        style={{
                          color:
                            dayOffset >= MAX_AVAIL_HISTORY_DAY_OFFSET
                              ? '#CACFD8'
                              : '#131517',
                        }}>
                        Prev 24h
                      </span>
                    </button>
                    <button
                      type="button"
                      disabled={dayOffset <= 0}
                      className={`${timelineNavStyles.timelineNavBtn} ${timelineNavStyles.timelineNavBtnDay}`}
                      onClick={goNextDay}>
                      <span
                        style={{
                          color: dayOffset <= 0 ? '#CACFD8' : '#131517',
                        }}>
                        Next 24h
                      </span>
                      <ReactSVG
                        src={Icons.rightArrow}
                        className={timelineNavStyles.timelineNavArrow}
                        beforeInjection={(svg) => {
                          svg.querySelectorAll('path').forEach((path) => {
                            path.setAttribute(
                              'stroke',
                              dayOffset <= 0 ? '#CACFD8' : '#131517'
                            );
                          });
                        }}
                      />
                    </button>
                  </div>

                  {orderedHistories.length > 1 ? (
                    <div className={tableNavStyles.buttonContainer}>
                      <button
                        type="button"
                        disabled={timelineScrollEdges.atStart}
                        className={timelineNavStyles.timelineNavBtn}
                        onClick={() => scrollTimeline(-1)}>
                        <ReactSVG
                          src={Icons.leftArrow}
                          className={timelineNavStyles.timelineNavArrow}
                          beforeInjection={(svg) => {
                            svg.querySelectorAll('path').forEach((path) => {
                              path.setAttribute(
                                'stroke',
                                timelineScrollEdges.atStart
                                  ? '#CACFD8'
                                  : '#131517'
                              );
                            });
                          }}
                        />
                        <span
                          style={{
                            color: timelineScrollEdges.atStart
                              ? '#CACFD8'
                              : '#131517',
                          }}>
                          Prev
                        </span>
                      </button>
                      <button
                        type="button"
                        disabled={timelineScrollEdges.atEnd}
                        className={timelineNavStyles.timelineNavBtn}
                        onClick={() => scrollTimeline(1)}>
                        <span
                          style={{
                            color: timelineScrollEdges.atEnd
                              ? '#CACFD8'
                              : '#131517',
                          }}>
                          Next
                        </span>
                        <ReactSVG
                          src={Icons.rightArrow}
                          className={timelineNavStyles.timelineNavArrow}
                          beforeInjection={(svg) => {
                            svg.querySelectorAll('path').forEach((path) => {
                              path.setAttribute(
                                'stroke',
                                timelineScrollEdges.atEnd
                                  ? '#CACFD8'
                                  : '#131517'
                              );
                            });
                          }}
                        />
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {/* Timeline (Figma: horizontal circles to inspect snapshots) */}
            {histories && Array.isArray(histories.data) ? (
              <div
                ref={timelineScrollRef}
                className={cls.timelineScroll}
                style={{
                  marginTop: 12,
                  width: '100%',
                  maxWidth: '100%',
                  border: 'none',
                  borderRadius: 0,
                  background: 'transparent',
                  padding: 0,
                  minWidth: 0,
                  boxSizing: 'border-box',
                  display: 'block',
                  // Scroll must happen only inside the inner timeline strip
                  overflowX: 'visible',
                  overflowY: 'visible',
                }}>
                <div
                  style={{
                    position: 'relative',
                    height: timelineInnerHeight,
                    // width: timelineInnerWidth,
                    // Only width is needed for horizontal scroll; don't force minWidth.
                    // But ensure the timeline never shrinks smaller than the viewport.
                    // minWidth: '100vw',
                    padding: 20,
                    overflowX: 'auto',
                    overflowY: 'visible',
                    // maxWidth: '100vw',
                    boxSizing: 'content-box',
                  }}
                  ref={timelineInnerScrollRef}
                  onScroll={refreshTimelineScrollEdges}
                  data-max-circles-per-hour={maxCirclesInAnyHour}
                  data-pixels-per-hour={pixelsPerHourPx}
                  data-timeline-track-width={timelineTrackWidthPx}>
                  {/* Track line */}
                  <div
                    style={{
                      position: 'absolute',
                      left: TIMELINE_PADDING_PX,
                      width: timelineTrackWidthPx,
                      minWidth: '99%',
                      top: TIMELINE_TRACK_LINE_TOP_PX,
                      height: TIMELINE_TRACK_LINE_HEIGHT_PX,
                      background: '#E8E8E8',
                      borderRadius: 2,
                      zIndex: 0,
                    }}
                  />

                  {/* Hour ticks (spacing = pixelsPerHourPx) */}
                  {Array.from({ length: TIMELINE_HOURS + 1 }, (_, i) => (
                    <div
                      key={i}
                      style={{
                        position: 'absolute',
                        left: TIMELINE_PADDING_PX + i * pixelsPerHourPx,
                        top: TIMELINE_TICK_TOP_PX,
                        width: 1,
                        height: TIMELINE_TICK_HEIGHT_PX,
                        background: '#E0E0E0',
                        borderRadius: 1,
                        transform: 'translateX(-50%)',
                        zIndex: 0,
                      }}
                    />
                  ))}

                  {(() => {
                    if (!showNowOnTimeline) return null;
                    const circleSize = TIMELINE_BUTTON_CIRCLE_SIZE_PX;
                    const top = TIMELINE_BUTTON_BASE_TOP_PX;
                    const x = nowDotCenterX;
                    const isNowSelected = selectedHistoryId == null;
                    const isHovered =
                      hoveredHistory != null &&
                      hoveredHistory.__availNowDot === true;
                    const isHighlighted = isNowSelected || isHovered;
                    return (
                      <div
                        key="avail-timeline-now-dot"
                        style={{
                          position: 'absolute',
                          left: x,
                          top,
                          transform: 'translateX(-50%)',
                          width: circleSize,
                          minWidth: circleSize,
                          padding: 0,
                          margin: 0,
                          border: 'none',
                          background: 'transparent',
                          overflow: 'visible',
                          zIndex: isHighlighted ? VERY_HIGH_ZINDEX : 3,
                          pointerEvents: 'none',
                        }}>
                        <button
                          type="button"
                          data-avail-history-id={AVAIL_HISTORY_NOW_DOT_ID}
                          onMouseEnter={(e) => {
                            const snap = nowSnapshotRef.current?.currentHistory;
                            const rect =
                              e.currentTarget.getBoundingClientRect();
                            setHoverTooltipPos({
                              top: rect.top - 8,
                              left: rect.left + rect.width / 2,
                            });
                            if (snap) {
                              setHoveredHistory({
                                ...snap,
                                __availNowDot: true,
                              });
                            } else {
                              setHoveredHistory(null);
                            }
                          }}
                          onClick={() => {
                            handleSelectNowSnapshot();
                          }}
                          onMouseLeave={() => {
                            setHoveredHistory((h) =>
                              h && h.__availNowDot ? null : h
                            );
                          }}
                          style={{
                            pointerEvents: 'auto',
                            width: circleSize,
                            height: circleSize,
                            padding: 0,
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            borderRadius: '50%',
                            display: 'block',
                            position: 'relative',
                            zIndex: isHighlighted ? VERY_HIGH_ZINDEX : 3,
                          }}
                          aria-label="Now (current availability)">
                          <div
                            style={{
                              width: circleSize,
                              height: circleSize,
                              borderRadius: '50%',
                              boxSizing: 'border-box',
                              background: 'transparent',
                              boxShadow: isNowSelected
                                ? '0 0 0 4px rgba(245, 197, 66, 0.25)'
                                : 'none',
                              border: isNowSelected
                                ? '2px solid #F5C542'
                                : `2px solid ${TIMELINE_NOW_DOT_BORDER}`,
                              pointerEvents: 'none',
                            }}
                          />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setHoveredHistory((h) =>
                              h && h.__availNowDot ? null : h
                            );
                            handleSelectNowSnapshot();
                          }}
                          style={{
                            position: 'absolute',
                            top:
                              circleSize +
                              Math.round(TIMELINE_BUTTON_CIRCLE_SIZE_PX * 1.45),
                            left: '50%',
                            transform: 'translateX(-68%) rotate(290deg)',
                            transformOrigin: 'center',
                            fontSize: 9,
                            fontFamily: 'Averta',
                            color: '#8B93A6',
                            whiteSpace: 'nowrap',
                            overflow: 'visible',
                            maxWidth: 'none',
                            zIndex: isHighlighted ? VERY_HIGH_ZINDEX : 4,
                            pointerEvents: 'auto',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            margin: 0,
                            lineHeight: 1.1,
                          }}>
                          Now
                        </button>
                      </div>
                    );
                  })()}

                  {orderedHistories.map((item, idx) => {
                    const layout = timelineLayout?.[idx];
                    const slotsLen = Array.isArray(item?.slots)
                      ? item.slots.length
                      : 0;
                    const hasSlots = slotsLen > 0;
                    const x =
                      layout?.x ??
                      timelinePositions?.[idx] ??
                      TIMELINE_PADDING_PX;
                    const top = TIMELINE_BUTTON_BASE_TOP_PX;
                    const circleSize =
                      layout?.size ?? TIMELINE_BUTTON_CIRCLE_SIZE_PX;

                    const isSelected =
                      item?.id != null && item?.id === selectedHistoryId;
                    const isHovered =
                      hoveredHistory != null &&
                      (hoveredHistory?.id != null && item?.id != null
                        ? hoveredHistory.id === item.id
                        : hoveredHistory === item);
                    const isHighlighted = isSelected || isHovered;

                    const apiCircleColor = item?.color;
                    const mappedCircle =
                      getTimelineCirclePaintFromApiColor(apiCircleColor);
                    let circleBackground;
                    let circleIdleBorder;
                    if (mappedCircle) {
                      circleBackground = mappedCircle.background;
                      circleIdleBorder = mappedCircle.border;
                    } else if (
                      apiCircleColor != null &&
                      String(apiCircleColor).trim() !== ''
                    ) {
                      circleBackground = String(apiCircleColor).trim();
                      circleIdleBorder = '2px solid transparent';
                    } else {
                      circleBackground = hasSlots
                        ? TIMELINE_COLOR_GREEN_SOLID
                        : TIMELINE_COLOR_GRAY_SOLID;
                      circleIdleBorder = '2px solid transparent';
                    }
                    const terminalLabel =
                      item?.terminal?.slug ||
                      item?.terminal?.name ||
                      (item?.terminal_id != null
                        ? String(item.terminal_id)
                        : '');
                    const terminalLabelWithAvailCount = terminalLabel
                      ? slotsLen > 0
                        ? `${terminalLabel} (${slotsLen})`
                        : terminalLabel
                      : '';
                    const slot0 = Array.isArray(item?.slots)
                      ? item.slots?.[0]
                      : null;
                    // Below circle: always prefer created_at (UTC from API) shown in user's local time.
                    const dateTimeLabel =
                      formatCreatedAtLocalLabel(item) ||
                      extractDateTimeShortLabel(slot0) ||
                      '';

                    return (
                      <div
                        key={item?.id ?? idx}
                        style={{
                          position: 'absolute',
                          left: x,
                          top,
                          transform: 'translateX(-50%)',
                          width: circleSize,
                          minWidth: circleSize,
                          padding: 0,
                          margin: 0,
                          border: 'none',
                          background: 'transparent',
                          overflow: 'visible',
                          zIndex: isHighlighted ? VERY_HIGH_ZINDEX : 1,
                          pointerEvents: 'none',
                        }}>
                        {/* Tooltip با hover/کلیک روی دایره؛ نه روی تاریخ؛ data-id فقط روی دایره */}
                        <button
                          type="button"
                          data-avail-history-id={item?.id ?? idx}
                          onMouseEnter={(e) => {
                            const rect =
                              e.currentTarget.getBoundingClientRect();
                            setHoverTooltipPos({
                              top: rect.top - 8,
                              left: rect.left + rect.width / 2,
                            });
                            setHoveredHistory(item);
                          }}
                          onClick={(e) => {
                            const rect =
                              e.currentTarget.getBoundingClientRect();
                            setHoverTooltipPos({
                              top: rect.top - 8,
                              left: rect.left + rect.width / 2,
                            });
                            setHoveredHistory(item);
                            handleSelectHistory(item);
                          }}
                          onMouseLeave={() => {
                            setHoveredHistory(null);
                          }}
                          style={{
                            pointerEvents: 'auto',
                            width: circleSize,
                            height: circleSize,
                            padding: 0,
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            borderRadius: '50%',
                            display: 'block',
                            position: 'relative',
                            zIndex: isHighlighted ? VERY_HIGH_ZINDEX : 1,
                          }}
                          aria-label={
                            terminalLabel
                              ? slotsLen > 0
                                ? `Snapshot ${terminalLabel} (${slotsLen} avail)`
                                : `Snapshot ${terminalLabel}`
                              : 'Snapshot'
                          }>
                          <div
                            style={{
                              width: circleSize,
                              height: circleSize,
                              borderRadius: '50%',
                              boxSizing: 'border-box',
                              background: circleBackground,
                              boxShadow: isHighlighted
                                ? '0 0 0 4px rgba(245, 197, 66, 0.25)'
                                : 'none',
                              border: isHighlighted
                                ? '2px solid #F5C542'
                                : circleIdleBorder,
                              pointerEvents: 'none',
                            }}
                          />
                        </button>
                        <div
                          style={{
                            position: 'absolute',
                            left: '50%',
                            bottom: '100%',
                            marginBottom: 3,
                            width: 0,
                            height: 0,
                            overflow: 'visible',
                            pointerEvents: 'none',
                            zIndex: isHighlighted ? VERY_HIGH_ZINDEX : 2,
                          }}>
                          <span
                            style={{
                              position: 'absolute',
                              left: 0,
                              bottom: 0,
                              display: 'block',
                              fontSize: 10,
                              fontFamily: 'Averta',
                              color: '#8B93A6',
                              whiteSpace: 'nowrap',
                              lineHeight: 1.1,
                              transform: 'translate(3px) rotate(290deg)',
                              transformOrigin: 'left bottom',
                              overflow: 'visible',
                              maxWidth: 'none',
                            }}>
                            {terminalLabelWithAvailCount}
                          </span>
                        </div>
                        {dateTimeLabel ? (
                          <button
                            type="button"
                            onClick={() => {
                              setHoveredHistory(null);
                              handleSelectHistory(item);
                            }}
                            style={{
                              position: 'absolute',
                              top:
                                circleSize +
                                Math.round(
                                  TIMELINE_BUTTON_CIRCLE_SIZE_PX * 1.45
                                ),
                              left: '50%',
                              transform: 'translateX(-68%) rotate(290deg)',
                              transformOrigin: 'center',
                              fontSize: 9,
                              fontFamily: 'Averta',
                              color: '#8B93A6',
                              whiteSpace: 'nowrap',
                              overflow: 'visible',
                              maxWidth: 'none',
                              zIndex: isHighlighted ? VERY_HIGH_ZINDEX : 2,
                              pointerEvents: 'auto',
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              padding: 0,
                              margin: 0,
                              lineHeight: 1.1,
                            }}>
                            {dateTimeLabel}
                          </button>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {renderHistoryHoverTooltip()}

            {/* Timeline dot legend (matches getTimelineCirclePaintFromApiColor) */}
            <div
              style={{
                marginTop: 10,
                padding: '0 6px',
                fontFamily: 'Averta',
                color: '#2b303b',
              }}>
              <p
                style={{
                  margin: '0 0 10px',
                  fontSize: 12,
                  lineHeight: 1.5,
                  color: '#50565E',
                }}>
                Each dot is a history point.{' '}
                <strong>
                  Click a dot to open the availability snapshot for that moment.
                </strong>{' '}
                Colours come from the API and are shown on the timeline as
                follows:
              </p>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '10px 18px',
                  alignItems: 'center',
                  fontSize: 11,
                  lineHeight: 1.35,
                }}>
                {[
                  {
                    label: 'Availability, with slots',
                    bg: TIMELINE_COLOR_GREEN_SOLID,
                    border: '2px solid transparent',
                  },
                  {
                    label: 'Availability, empty',
                    bg: TIMELINE_COLOR_GRAY_SOLID,
                    border: '2px solid transparent',
                  },

                  {
                    label: 'User, with slots',
                    bg: TIMELINE_COLOR_BORDER_GREEN_FILL,
                    border: `2px solid ${TIMELINE_COLOR_GREEN_RING}`,
                  },
                  {
                    label: 'User, empty',
                    bg: TIMELINE_COLOR_BORDER_GRAY_FILL,
                    border: `2px solid ${TIMELINE_COLOR_GRAY_RING}`,
                  },
                  {
                    label: 'Now',
                    bg: 'transparent',
                    border: `2px solid ${TIMELINE_NOW_DOT_BORDER}`,
                  },
                ].map((row) => (
                  <span
                    key={row.label}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      maxWidth: '100%',
                    }}>
                    <span
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        boxSizing: 'border-box',
                        background: row.bg,
                        border: row.border,
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ whiteSpace: 'nowrap' }}>{row.label}</span>
                  </span>
                ))}
              </div>
            </div>

            {/* بنر فقط وقتی یک snapshot از تایم‌لاین انتخاب شده؛ در حالت پیش‌فرض «now» چیزی زیر تایم‌لاین نیست. */}
            {selectedHistoryId != null ? (
              <div
                style={{
                  marginTop: 10,
                  padding: '8px 12px',
                  borderRadius: 4,
                  background: '#eaf7f0',
                  fontSize: 12,
                  fontFamily: 'Averta',
                  color: '#2b303b',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 10,
                  minHeight: 34,
                  flexWrap: 'nowrap',
                }}>
                <span
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flex: 1,
                  }}>
                  {currentHistory?.slots?.length ? (
                    <>
                      Snapshot: {currentHistory.slots?.[0]}{' '}
                      {`(${currentHistory.slots.length} slots available`}
                      {(() => {
                        const tc = getTerminalsCheckedCount(currentHistory);
                        return typeof tc === 'number'
                          ? `, ${tc} terminals checked)`
                          : ')';
                      })()}{' '}
                      {`Highlighting: Today ${shift ? '1st' : '2nd'} shift`}
                    </>
                  ) : (
                    'Viewing selected history'
                  )}
                </span>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    flexShrink: 0,
                  }}>
                  <button
                    type="button"
                    aria-label="Back to Now"
                    onClick={handleClearSnapshot}
                    style={{
                      border: '1px solid #1fc16b',
                      background: '#fff',
                      color: '#0d9f54',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontFamily: 'Averta',
                      fontWeight: 700,
                      padding: '8px 16px',
                      borderRadius: 8,
                      whiteSpace: 'nowrap',
                      lineHeight: 1.2,
                      boxShadow: '0 1px 2px rgba(31, 193, 107, 0.12)',
                      transition: 'background 0.15s ease, color 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#1fc16b';
                      e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#fff';
                      e.currentTarget.style.color = '#0d9f54';
                    }}>
                    Back to Now
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div
            style={{
              position: 'relative',
              marginTop: 12,
              marginRight: 2,
              ...(availabilityTableLoading ? { minHeight: 240 } : null),
            }}>
            {availabilityTableLoading ? (
              <div
                role="status"
                aria-live="polite"
                aria-busy="true"
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 30,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255, 255, 255, 0.78)',
                  borderRadius: 4,
                  pointerEvents: 'auto',
                }}>
                <Icon src={Icons.loadingIndicatorGray} width="52px" />
                <span
                  style={{
                    marginTop: 10,
                    fontSize: 13,
                    fontFamily: 'Averta',
                    color: '#717784',
                  }}>
                  Loading availability…
                </span>
              </div>
            ) : null}
            <AppointmentTable
              data={appointmentData}
              subTitle={''}
              shift={shift}
              loading={availabilityTableLoading}
              cabinId={cabinId}
              setSelected={setSelected}
              containerName={selected?.Container?.label}
              Container={selected?.Container}
              SSL={selected?.SSL?.label}
              Size={selected?.Size?.label}
              availHistoryGetSnapshotAvailabilities={
                availHistoryGetSnapshotAvailabilities
              }
              availHistoryUseSnapshotModal={selectedHistoryId != null}
              availHistoryOnOpenSnapshotModal={openAvailabilitySnapshotModal}
              availHistoryDisableBooking
              availHistoryRestrictClicksToTerminalId={
                availHistoryRestrictClicksToTerminalId
              }
            />
          </div>

          <AvailabilitySnapshotModal
            open={Boolean(availabilitySnapshotModal)}
            onClose={() => setAvailabilitySnapshotModal(null)}
            terminalSlug={availabilitySnapshotModal?.terminalSlug}
            rows={availabilitySnapshotModal?.rows || []}
          />
        </>
      )}
    </div>
  );
};

export default AvailHistory;
